import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  Collapse,
  Empty,
  Input,
  message,
  Modal,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';

import api from '../../../../../../services/api';

import * as S from './styles';

const { Panel } = Collapse;
const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const STATUS_REFERENCIA = {
  PENDENTE_VALIDACAO: 1,
  APROVADA: 2,
  REPROVADA: 3,
};

const statusReferencia = {
  [STATUS_REFERENCIA.PENDENTE_VALIDACAO]: {
    color: 'gold',
    descricao: 'Pendente',
  },
  [STATUS_REFERENCIA.APROVADA]: {
    color: 'green',
    descricao: 'Aprovada',
  },
  [STATUS_REFERENCIA.REPROVADA]: {
    color: 'red',
    descricao: 'Reprovada',
  },
};

export function AnaliseReferencias({
  idConsultaSolicitacao,
  nomeConsulta,
  orientacao,
}) {
  const [modalVisivel, setModalVisivel] = useState(false);
  const [modalObservacaoVisivel, setModalObservacaoVisivel] = useState(false);
  const [referencias, setReferencias] = useState([]);
  const [referenciaSelecionada, setReferenciaSelecionada] = useState(null);
  const [statusSelecionado, setStatusSelecionado] = useState(null);
  const [observacao, setObservacao] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const buscarReferencias = useCallback(async () => {
    setCarregando(true);

    try {
      const response = await api.get(
        `/referencia-motorista-consulta/${idConsultaSolicitacao}`
      );
      setReferencias(response?.data || []);
    } catch (error) {
      setReferencias([]);
      message.error(
        error?.response?.data?.detail ||
          'Não foi possível carregar as referências.'
      );
    } finally {
      setCarregando(false);
    }
  }, [idConsultaSolicitacao]);

  useEffect(() => {
    if (modalVisivel) buscarReferencias();
  }, [buscarReferencias, modalVisivel]);

  const abrirModalObservacao = (event, referencia, status) => {
    event.stopPropagation();
    setReferenciaSelecionada(referencia);
    setStatusSelecionado(status);
    setObservacao('');
    setModalObservacaoVisivel(true);
  };

  const limparModalObservacao = () => {
    setModalObservacaoVisivel(false);
    setReferenciaSelecionada(null);
    setStatusSelecionado(null);
    setObservacao('');
  };

  const fecharModalObservacao = () => {
    if (salvando) return;

    limparModalObservacao();
  };

  const atualizarStatusReferencia = async () => {
    if (!observacao.trim()) {
      message.warning('Informe uma observação para confirmar a análise.');
      return;
    }

    const payload = {
      idConsultaSolicitacao,
      statusReferencia: statusSelecionado,
      observacao: observacao.trim(),
    };

    setSalvando(true);

    try {
      await api.put(
        `/referencia-motorista-consulta/status/${referenciaSelecionada.id}`,
        payload
      );
      message.success(
        statusSelecionado === STATUS_REFERENCIA.APROVADA
          ? 'Referência aprovada com sucesso.'
          : 'Referência reprovada com sucesso.'
      );
      limparModalObservacao();
      await buscarReferencias();
    } catch (error) {
      message.error(
        error?.response?.data?.detail ||
          'Não foi possível atualizar o status da referência.'
      );
    } finally {
      setSalvando(false);
    }
  };

  const fecharModalReferencias = () => {
    if (salvando) return;

    fecharModalObservacao();
    setModalVisivel(false);
  };

  const renderCabecalhoReferencia = referencia => {
    const status = statusReferencia[referencia.idStatusReferencia];

    return (
      <S.ReferenceHeader>
        <S.ReferenceData>
          <S.ReferenceField>
            <Text type="secondary">Nome</Text>
            <Text>{referencia.nome || '-'}</Text>
          </S.ReferenceField>
          <S.ReferenceField>
            <Text type="secondary">Tipo</Text>
            <Text>{referencia.tipoReferencia || '-'}</Text>
          </S.ReferenceField>
          <S.ReferenceField>
            <Text type="secondary">Telefone</Text>
            <Text>{referencia.telefone || '-'}</Text>
          </S.ReferenceField>
        </S.ReferenceData>

        <S.ReferenceActions>
          {status && <Tag color={status.color}>{status.descricao}</Tag>}
          <Button
            icon={<CheckOutlined />}
            className="approve-button"
            type={
              referencia.idStatusReferencia === STATUS_REFERENCIA.APROVADA
                ? 'primary'
                : 'default'
            }
            onClick={event =>
              abrirModalObservacao(
                event,
                referencia,
                STATUS_REFERENCIA.APROVADA
              )
            }
          >
            Aprovar
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            type={
              referencia.idStatusReferencia === STATUS_REFERENCIA.REPROVADA
                ? 'primary'
                : 'default'
            }
            onClick={event =>
              abrirModalObservacao(
                event,
                referencia,
                STATUS_REFERENCIA.REPROVADA
              )
            }
          >
            Reprovar
          </Button>
        </S.ReferenceActions>
      </S.ReferenceHeader>
    );
  };

  const acaoSelecionada =
    statusSelecionado === STATUS_REFERENCIA.APROVADA
      ? 'aprovação'
      : 'reprovação';

  return (
    <>
      <Button type="primary" onClick={() => setModalVisivel(true)}>
        Analisar referências
      </Button>

      <Modal
        title={nomeConsulta || 'Análise de referências'}
        open={modalVisivel}
        width={900}
        footer={null}
        destroyOnHidden
        onCancel={fecharModalReferencias}
      >
        <S.Orientation>
          <Text strong>Orientação</Text>
          <Paragraph>{orientacao || '-'}</Paragraph>
        </S.Orientation>

        <Spin spinning={carregando} tip="Carregando referências...">
          <S.ReferencesContent>
            {!carregando && referencias.length === 0 ? (
              <Empty description="Nenhuma referência encontrada." />
            ) : (
              <S.ReferencesCollapse
                bordered={false}
                expandIconPosition="right"
                expandIcon={({ isActive }) => (
                  <DownOutlined rotate={isActive ? 180 : 0} />
                )}
              >
                {referencias.map(referencia => (
                  <Panel
                    header={renderCabecalhoReferencia(referencia)}
                    key={referencia.id}
                  >
                    <S.ReferenceObservation>
                      {referencia.usuarioAnalise && (
                        <div>
                          <Text strong>Usuário análise: </Text>
                          <Text>{referencia.usuarioAnalise}</Text>
                        </div>
                      )}
                      {referencia.dataStatus && (
                        <div>
                          <Text strong>Data: </Text>
                          <Text>
                            {moment(referencia.dataStatus).format(
                              'DD/MM/YYYY HH:mm:ss'
                            )}
                          </Text>
                        </div>
                      )}
                      <div>
                        <Text strong>Observação: </Text>
                        <Text>
                          {referencia.observacaoStatus ||
                            'Nenhuma observação informada.'}
                        </Text>
                      </div>
                    </S.ReferenceObservation>
                  </Panel>
                ))}
              </S.ReferencesCollapse>
            )}
          </S.ReferencesContent>
        </Spin>
      </Modal>

      <Modal
        title={`Confirmar ${acaoSelecionada}`}
        open={modalObservacaoVisivel}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={salvando}
        okButtonProps={{
          danger: statusSelecionado === STATUS_REFERENCIA.REPROVADA,
        }}
        destroyOnHidden
        onOk={atualizarStatusReferencia}
        onCancel={fecharModalObservacao}
      >
        <Paragraph>
          {`Informe a observação da ${acaoSelecionada} de `}
          <Text strong>
            {referenciaSelecionada?.nome || 'referência'}
          </Text>
          .
        </Paragraph>
        <TextArea
          rows={4}
          placeholder="Digite a observação"
          value={observacao}
          onChange={event => setObservacao(event.target.value)}
        />
      </Modal>
    </>
  );
}

AnaliseReferencias.propTypes = {
  idConsultaSolicitacao: PropTypes.number.isRequired,
  nomeConsulta: PropTypes.string,
  orientacao: PropTypes.string,
};

AnaliseReferencias.defaultProps = {
  nomeConsulta: '',
  orientacao: '',
};
